import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PageLayout } from '@/components/ui/page-layout';
import { motion } from 'framer-motion';
import { 
  Lightbulb,
  Brain,
  Heart,
  Star,
  ArrowRight,
  Calendar,
  Clock,
  CheckCircle,
  MessageCircle
} from 'lucide-react';
import { LectureDesignModal } from '@/components/modals/LectureDesignModal';

const features = [
  {
    title: "Votre carte HED personnalisée basée sur vos données de naissance",
    description: "Une analyse détaillée de votre design unique, calculée à partir de vos données de naissance précises.",
    icon: <Star className="w-6 h-6 text-amber-600" />
  },
  {
    title: "Une analyse détaillée de votre design énergétique",
    description: "Comprendre en profondeur votre type, votre autorité, vos centres et vos canaux énergétiques.",
    icon: <Brain className="w-6 h-6 text-purple-600" />
  },
  {
    title: "Des pistes concrètes d'alignement adaptées à votre profil",
    description: "Des recommandations pratiques pour vivre en harmonie avec votre design unique.",
    icon: <Heart className="w-6 h-6 text-pink-600" />
  },
  {
    title: "Un support écrit pour garder une trace de votre lecture",
    description: "Un document complet reprenant tous les points clés de votre lecture pour vous y référer plus tard.",
    icon: <CheckCircle className="w-6 h-6 text-green-600" />
  }
];

export default function LectureDesignPage() {
  const [showModal, setShowModal] = useState(false);

  return (
    <PageLayout showBackButton={false}>
      {/* Hero Section */}
      <div className="relative min-h-[80vh] flex items-center justify-center overflow-hidden px-4">
        {/* Background Image */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1519834785169-98be25ec3f84?q=80&w=3648&auto=format&fit=crop')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'brightness(0.3)'
          }}
        />

        {/* Gradient Overlay */}
        <div 
          className="absolute inset-0 z-1"
          style={{
            background: 'linear-gradient(to bottom, rgba(84, 7, 91, 0.9), rgba(147, 51, 234, 0.7))'
          }}
        />

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto text-center text-white px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="space-y-8"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="mb-12"
            >
              <div className="p-4 bg-white/10 backdrop-blur-sm rounded-2xl inline-block">
                <Lightbulb className="w-12 h-12 sm:w-16 sm:h-16 text-white" />
              </div>
            </motion.div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Lecture de Design
            </h1>
            
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Découvrez votre profil énergétique unique et votre potentiel inexploité
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
              <Button 
                onClick={() => setShowModal(true)}
                className="w-full sm:w-auto bg-white text-purple-900 hover:bg-white/90 px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Réserver votre lecture
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-20 px-4 bg-white/70 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-white/90 backdrop-blur-sm">
            <CardContent className="p-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Recevoir son profil énergétique, c'est se donner la permission de mieux se comprendre. 
                Une lecture HED, c'est un miroir tendre et précis de qui vous êtes, de vos schémas naturels, 
                de vos besoins cachés, de votre fonctionnement profond.
              </p>

              <div className="space-y-8">
                <h2 className="text-2xl font-bold text-[#54075B] mb-6">Ce que vous recevrez :</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {features.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex gap-4"
                    >
                      <div className="shrink-0">
                        <div className="p-3 bg-white rounded-xl shadow-sm">
                          {feature.icon}
                        </div>
                      </div>
                      <div>
                        <h3 className="font-medium mb-2">{feature.title}</h3>
                        <p className="text-sm text-gray-600">{feature.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Booking Section */}
      <div className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Card className="bg-gradient-to-r from-[#54075B] to-purple-700 text-white">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-4">Prêt(e) à découvrir votre design ?</h2>
              <div className="flex items-center justify-center gap-4 mb-6 text-lg">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  <span>2h de lecture</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span>Support inclus</span>
                </div>
              </div>
              <p className="text-lg mb-8">
                Réservez votre lecture personnalisée et commencez votre voyage vers une vie plus alignée.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => setShowModal(true)}
                  variant="secondary"
                  size="lg"
                  className="gap-2"
                >
                  Réserver ma lecture
                  <ArrowRight className="w-5 h-5" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="text-white border-white hover:bg-white/10 gap-2"
                  onClick={() => window.location.href = "https://wa.me/votre-numero"}
                >
                  <MessageCircle className="w-5 h-5" />
                  Discuter avec une assistante
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal */}
      <LectureDesignModal 
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
    </PageLayout>
  );
}